/**
 * Price Per Can Calculator
 * 
 * This script automatically calculates and displays the price per can for product variants.
 * It supports both custom pricing (for specific products) and automatic calculation based on
 * variant price and pack size.
 * 
 * Features:
 * - Parses pack size from variant titles (e.g., "Yellow12" → 12 cans)
 * - Custom pricing configuration for specific products
 * - Automatic price calculation for products without custom pricing
 * - Works with multivariant products (size + flavor combinations)
 * - Automatically creates display element if it doesn't exist
 * 
 * Usage:
 * 1. Add product-specific pricing to PRODUCT_PRICING object
 * 2. Include this script in your product template
 * 3. Ensure variant titles include pack size numbers
 * 
 * @author NotewareDigital.com
 * @version 2.0
 * @date 2025
 */

// Product-specific pricing configuration
// Add new products here with their custom pricing
const PRODUCT_PRICING = {
    "6554019201156": { // Yellowfin in Olive Oil Sub
        name: "Yellowfin in Olive Oil Sub",
        12: "($3.52/can)",
        24: "($3.08/can)"
    },
    "6878787108996": {
        name: "Product 6878787108996",
        12: "($4.40/can)",
        24: "($3.85/can)"
    },
    "6701668335748": {
        name: "Product 6701668335748", 
        12: "($3.60/can)",
        24: "($3.15/can)"
    },
    "5008666689668": { // Yellowfin in Olive Oil
        name: "Yellowfin in Olive Oil",
        12: "($4.40/can)",
        24: "($3.85/can)"
    },
    "6678183313540": { // Chili
        name: "Chili",
        12: "($4.40/can)",
        24: "($3.85/can)"
    },

    "6701672005764": { // Alb Sub
        name: "Alb Sub",
        12: "($3.52/can)",
        24: "($3.08/can)"
    },
    "6756408623236": { // Sardines sub
        name: "Sardines sub",
        12: "($3.10/can)",
        24: "($2.64/can)"
    },
    "6726740770948": { // Sardines reg
        name: "Sardines reg",
        12: "($3.85/can)",
        24: "($3.30/can)"
    },
    "7044828692612": { // Bean deals
        name: "Bean deals",
        12: "($4.40/can)",
        24: "($3.85/can)"
    },
    "7199396921476": { // Mack EVOO
        name: "Mack EVOO",
        12: "($3.85/can)",
        24: "($3.30/can)"
    },
    "7212440715396": { // Fiesta Salad
        name: "Fiesta Salad",
        12: "($4.40/can)",
        24: "($3.85/can)"
    },
    "6678197502084": { // WHITE Albacore in Extra Virgin Olive Oil Sub&Save 20%
        name: "WHITE Albacore in Extra Virgin Olive Oil Sub&Save 20%",
        12: "($3.52/can)",
        24: "($3.08/can)"
    },

    // Additional products from CSV export
    "natural-catch-tuna-white-albacore-in-extra-virgin-olive-oil": {
        name: "WHITE Albacore in Extra Virgin Olive Oil",
        6: "($6.00/can)",   // $35.99 ÷ 6 = $5.998
        12: "($5.00/can)",  // $59.95 ÷ 12 = $4.996
        24: "($4.06/can)"   // $97.40 ÷ 24 = $4.058
    },

    "natural-catch-tuna-special-offer": {
        name: "Special Offer (Multi-Variant)",
        6: "($5.49/can)",   // $32.95 ÷ 6 = $5.492
        12: "($4.40/can)",  // $52.80 ÷ 12 = $4.40
        24: "($3.85/can)"   // $92.40 ÷ 24 = $3.85
    },

    "natural-catch-tuna-products": {
        name: "Tuna Products (Multi-Variant)",
        6: "($5.49/can)",   // $32.95 ÷ 6 = $5.492
        12: "($4.40/can)",  // $52.80 ÷ 12 = $4.40
        24: "($3.85/can)"   // $92.40 ÷ 24 = $3.85
    },

    "natural-catch-tuna-white-albacore-in-water": {
        name: "WHITE Albacore in Water",
        6: "($5.49/can)",   // $32.95 ÷ 6 = $5.492
        12: "($4.40/can)",  // $52.80 ÷ 12 = $4.40
        24: "($3.85/can)"   // $92.40 ÷ 24 = $3.85
    },

    "natural-catch-tuna-spicy-chili": {
        name: "Spicy Chili",
        6: "($5.49/can)",   // $32.95 ÷ 6 = $5.492
        12: "($4.40/can)",  // $52.80 ÷ 12 = $4.40
        24: "($3.85/can)"   // $92.40 ÷ 24 = $3.85
    },

    "natural-catch-tuna-subscription": {
        name: "Yellowfin in Olive Oil Sub&Save",
        12: "($3.52/can)",  // $42.24 ÷ 12 = $3.52
        24: "($3.08/can)"   // $73.92 ÷ 24 = $3.08
    },

    "natural-catch-tuna": {
        name: "Yellowfin in Olive Oil",
        6: "($5.49/can)",   // $32.95 ÷ 6 = $5.492
        12: "($4.40/can)",  // $52.80 ÷ 12 = $4.40
        24: "($3.85/can)"   // $92.40 ÷ 24 = $3.85
    }
};

/**
 * Get custom price text for specific products
 * @param {string} productId - Shopify product ID
 * @param {number} packSize - Number of cans in the pack
 * @returns {string|null} Custom price text or null if not found
 */
function getCustomPriceText(productId, packSize) {
    const productConfig = PRODUCT_PRICING[productId];
    if (productConfig && productConfig[packSize]) {
        return productConfig[packSize];
    }
    return null;
}

/**
 * Calculate price per can from variant data
 * @param {string} selectedVariantTitle - Variant title (e.g., "Yellow12", "alb24")
 * @param {number} productPrice - Price in dollars (not cents)
 * @param {string} productId - Shopify product ID
 * @returns {string} Formatted price per can text
 */
function calculatePricePerCan(selectedVariantTitle, productPrice, productId) {
    console.log('Selected variant title:', selectedVariantTitle);
    
    // Parse pack size from variant title (e.g., "Yellow12", "alb24", "Spicy6")
    let packSize;
    
    // Look for number at the end of the string first (most reliable)
    const numberMatch = selectedVariantTitle.match(/(\d+)$/);
    if (numberMatch) {
        packSize = parseInt(numberMatch[1]);
    } else {
        // Fallback: look for any number in the string
        const anyNumberMatch = selectedVariantTitle.match(/(\d+)/);
        if (anyNumberMatch) {
            packSize = parseInt(anyNumberMatch[1]);
        } else {
            packSize = 1; // Default fallback for single items
        }
    }
    
    console.log('Parsed pack size:', packSize);
    
    // Check for custom pricing first (takes priority)
    const customPrice = getCustomPriceText(productId, packSize);
    if (customPrice) {
        console.log('Using custom price:', customPrice);
        return customPrice;
    }
    
    // Validate pack size for calculated pricing
    if (!packSize || packSize <= 0) {
        console.error('Invalid pack size:', packSize, 'from variant:', selectedVariantTitle);
        return '($0.00/can)';
    }
    
    // Calculate the price per can automatically
    const pricePerCan = productPrice / packSize;
    console.log('Calculated price per can:', pricePerCan);
    
    return `($${pricePerCan.toFixed(2)}/can)`;
}

/**
 * Update the price per can display element
 * @param {string} selectedVariantTitle - Variant title
 * @param {number} productPrice - Price in dollars
 * @param {string} productId - Shopify product ID
 */
function updatePricePerCanText(selectedVariantTitle, productPrice, productId) {
    const customTextElement = document.querySelector(".custom-text");
    if (!customTextElement) {
        console.error('Element with class "custom-text" not found');
        return;
    }

    // Get the price per can (either custom or calculated)
    const pricePerCanText = calculatePricePerCan(selectedVariantTitle, productPrice, productId);

    // Update the display element
    customTextElement.innerText = ` ${pricePerCanText}`;
    console.log('Updated price per can display:', pricePerCanText);
}

/**
 * Handle variant selection changes
 * @param {string} variantId - Selected variant ID
 */
function updateVariantPrice(variantId) {
    // Find the variant data from the select options
    const variantOption = document.querySelector(`option[value="${variantId}"]`);
    if (variantOption) {
        // Get the variant title from the option text (handleized version)
        const variantTitle = variantOption.textContent.trim();
        
        // Get the variant price from Shopify's variant data
        if (window.productVariants && window.currentProductId) {
            const variant = window.productVariants.find(v => v.id == variantId);
            if (variant) {
                const productPrice = variant.price / 100; // Convert from cents to dollars
                updatePricePerCanText(variantTitle, productPrice, window.currentProductId);
            }
        }
    }
}

/**
 * Initialize the price per can functionality
 * Sets up event listeners and displays initial price
 */
document.addEventListener("DOMContentLoaded", function() {
    console.log('Initializing price per can calculator...');

    // Get product data from the page's meta tags or global variables
    // These should be set by the Liquid template
    window.currentProductId = window.productData?.id || document.querySelector('meta[name="product-id"]')?.content;
    window.productVariants = window.productData?.variants || [];

    console.log('Product ID:', window.currentProductId);
    console.log('Product variants loaded:', window.productVariants);

    // Get the initial selected variant and display its price per can
    const currentVariantId = window.productData?.currentVariantId;
    const currentVariant = window.productVariants.find(v => v.id == currentVariantId);

    if (currentVariant) {
        console.log('Initial variant:', currentVariant);
        const productPrice = currentVariant.price / 100; // Convert from cents
        updatePricePerCanText(currentVariant.title, productPrice, window.currentProductId);
    } else {
        // Fallback: try to get variant from URL or form
        const urlParams = new URLSearchParams(window.location.search);
        const variantParam = urlParams.get('variant');
        if (variantParam && window.productVariants.length > 0) {
            const variant = window.productVariants.find(v => v.id == variantParam);
            if (variant) {
                const productPrice = variant.price / 100;
                updatePricePerCanText(variant.title, productPrice, window.currentProductId);
            }
        } else if (window.productVariants.length > 0) {
            // Use first variant as default
            const variant = window.productVariants[0];
            const productPrice = variant.price / 100;
            updatePricePerCanText(variant.title, productPrice, window.currentProductId);
        }
    }
    
    // Listen for variant changes on the select element (dropdown)
    const productSelect = document.getElementById("product-select");
    if (productSelect) {
        productSelect.addEventListener("change", function() {
            console.log('Variant changed via select:', this.value);
            updateVariantPrice(this.value);
        });
    }
    
    // Also listen for swatch clicks (color/size swatches)
    const packOptions = document.querySelectorAll(".nt-swatch");
    packOptions.forEach(option => {
        option.addEventListener("click", function() {
            const variantId = this.dataset.value || this.getAttribute('data-value');
            if (variantId) {
                console.log('Variant changed via swatch:', variantId);
                updateVariantPrice(variantId);
            }
        });
    });
    
    // Create custom-text element if it doesn't exist in the template
    // This ensures the script works even if the template doesn't have the element
    if (!document.querySelector(".custom-text")) {
        console.log('Creating custom-text element...');
        const priceElement = document.querySelector(".price.productPrice, #productPrice");
        if (priceElement) {
            const customTextSpan = document.createElement("div");
            customTextSpan.className = "custom-text";
            customTextSpan.style.fontWeight = "bold";
            customTextSpan.style.margin = "10px 0";
            customTextSpan.style.color = "#333";
            priceElement.parentNode.insertBefore(customTextSpan, priceElement.nextSibling);
            console.log('Custom-text element created and appended after price element');
        } else {
            console.warn('Could not find price element to append custom-text to');
        }
    }

    // Set up event listeners for variant changes
    // Listen for variant selector changes (common Shopify patterns)
    const variantSelectors = document.querySelectorAll('select[name="id"], input[name="id"], .variant-selector input, .variant-selector select');
    variantSelectors.forEach(selector => {
        selector.addEventListener('change', function() {
            console.log('Variant selector changed:', this.value);
            handleVariantChange(this.value);
        });
    });

    // Listen for variant swatch clicks (common in custom themes)
    const variantSwatches = document.querySelectorAll('.variant-swatch, .swatch-element, .variant-option, .size-selector a, .variant-option-value');
    variantSwatches.forEach(swatch => {
        swatch.addEventListener('click', function() {
            console.log('Variant swatch clicked:', this);
            // Small delay to allow variant to update
            setTimeout(() => {
                const selectedVariant = getSelectedVariant();
                if (selectedVariant) {
                    console.log('Selected variant after click:', selectedVariant);
                    handleVariantChange(selectedVariant.id);
                }
            }, 100);
        });
    });

    // Specific listener for this theme's size selector pattern
    const sizeLinks = document.querySelectorAll(
        '.swatches-select[data-attribute="pa_size"] a[href="javascript:void(0);"]'
    );
    sizeLinks.forEach(link => {
        link.addEventListener('click', function(e) {
            console.log('Size link clicked:', this.textContent);
            const sizeText = this.textContent.trim();

            // Prevent default behavior to ensure we control the update
            e.preventDefault();

            // Find the variant that matches this size immediately
            const matchingVariant = window.productVariants.find(variant => {
                const variantTitle = variant.title.toLowerCase();
                const clickedSize = sizeText.toLowerCase();
                return variantTitle.includes(clickedSize.replace(' ', '-')) ||
                       variantTitle.includes(clickedSize.replace(' ', ''));
            });

            if (matchingVariant) {
                console.log('Found matching variant for size:', sizeText, matchingVariant);

                // Update active state on size selector
                document.querySelectorAll('ul li a').forEach(a => a.classList.remove('active'));
                this.classList.add('active');

                // Handle the variant change (updates both price and price per can)
                handleVariantChange(matchingVariant.id);
            } else {
                console.warn('No matching variant found for size:', sizeText);
            }
        });
    });

    // Listen for custom variant change events that might be triggered by the theme
    document.addEventListener('variant:change', function(event) {
        console.log('Custom variant:change event detected:', event.detail);
        if (event.detail && event.detail.variant) {
            const variant = event.detail.variant;
            const productPrice = variant.price / 100;
            updatePricePerCanText(variant.title, productPrice, window.currentProductId);
        }
    });

    // Listen for Shopify's built-in variant change events
    document.addEventListener('variantChange', function(event) {
        console.log('Shopify variantChange event detected:', event.detail);
        if (event.detail && event.detail.variant) {
            const variant = event.detail.variant;
            const productPrice = variant.price / 100;
            updatePricePerCanText(variant.title, productPrice, window.currentProductId);
        }
    });

    // Watch for changes to the main price element (most reliable method)
    const priceElement = document.querySelector('#productPrice, .price.productPrice, .shopify-variation-price.price_varies.mb__25, .shopify-variation-price, .price_varies');
    if (priceElement) {
        console.log('Found price element to observe:', priceElement);

        const priceObserver = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.type === 'childList' || mutation.type === 'characterData') {
                    console.log('Price element content changed, updating price per can');

                    // Small delay to ensure the price has fully updated
                    setTimeout(() => {
                        // Try to determine which variant is now selected
                        const selectedVariant = getSelectedVariantFromPrice();
                        if (selectedVariant) {
                            console.log('Detected variant from price change:', selectedVariant);
                            const productPrice = selectedVariant.price / 100;
                            updatePricePerCanText(selectedVariant.title, productPrice, window.currentProductId);
                        }
                    }, 50);
                }
            });
        });

        // Start observing the price element
        priceObserver.observe(priceElement, {
            childList: true,
            subtree: true,
            characterData: true
        });

        console.log('Price observer set up successfully');

        // Also specifically watch the <ins> element that contains the current price
        const currentPriceElement = priceElement.querySelector('#product-price, ins');
        if (currentPriceElement) {
            const currentPriceObserver = new MutationObserver(function(mutations) {
                mutations.forEach(function(mutation) {
                    if (mutation.type === 'childList' || mutation.type === 'characterData') {
                        console.log('Current price element (ins) changed:', currentPriceElement.textContent);

                        setTimeout(() => {
                            const selectedVariant = getSelectedVariantFromPrice();
                            if (selectedVariant) {
                                console.log('Detected variant from current price change:', selectedVariant);
                                const productPrice = selectedVariant.price / 100;
                                updatePricePerCanText(selectedVariant.title, productPrice, window.currentProductId);
                            }
                        }, 50);
                    }
                });
            });

            currentPriceObserver.observe(currentPriceElement, {
                childList: true,
                subtree: true,
                characterData: true
            });

            console.log('Current price (ins) observer set up successfully');
        }
    } else {
        console.warn('Could not find price element to observe');
    }

    // Also observe the size selector for class changes (active state)
    const sizeSelector = document.querySelector('.size-selector, ul');
    if (sizeSelector) {
        const sizeObserver = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    console.log('Size selector class changed');
                    setTimeout(() => {
                        const selectedVariant = getSelectedVariantFromPrice();
                        if (selectedVariant) {
                            const productPrice = selectedVariant.price / 100;
                            updatePricePerCanText(selectedVariant.title, productPrice, window.currentProductId);
                        }
                    }, 100);
                }
            });
        });

        // Observe all size selector links for class changes
        const sizeLinks = sizeSelector.querySelectorAll('a, li');
        sizeLinks.forEach(link => {
            sizeObserver.observe(link, {
                attributes: true,
                attributeFilter: ['class']
            });
        });

        console.log('Size selector observer set up successfully');
    }

    console.log('Price per can calculator initialized successfully with variant change listeners');
});

/**
 * Get the currently selected variant from the page
 */
function getSelectedVariant() {
    // Try to find selected variant from various common selectors
    const variantSelector = document.querySelector('select[name="id"]');
    if (variantSelector && variantSelector.value) {
        const variantId = parseInt(variantSelector.value);
        return window.productVariants.find(v => v.id === variantId);
    }

    // Try to find from radio buttons
    const selectedRadio = document.querySelector('input[name="id"]:checked');
    if (selectedRadio && selectedRadio.value) {
        const variantId = parseInt(selectedRadio.value);
        return window.productVariants.find(v => v.id === variantId);
    }

    // Try to find from active swatch/option
    const activeOption = document.querySelector('.variant-option.active, .swatch-element.active, .size-selector .active');
    if (activeOption) {
        const variantTitle = activeOption.textContent.trim().toLowerCase();
        return window.productVariants.find(v => v.title.toLowerCase().includes(variantTitle));
    }

    // Fallback: return first variant
    return window.productVariants[0];
}

/**
 * Get the currently selected variant by matching the displayed price
 */
function getSelectedVariantFromPrice() {
    const priceElement = document.querySelector('#productPrice, .price.productPrice, .shopify-variation-price.price_varies.mb__25, .shopify-variation-price, .price_varies');
    if (!priceElement) {
        return getSelectedVariant(); // Fallback to other method
    }

    // Extract price from the element text - look specifically for the <ins> element which contains the current price
    const currentPriceElement = priceElement.querySelector('#product-price, ins') || priceElement;
    const priceText = currentPriceElement.textContent || currentPriceElement.innerText;
    console.log('Current price text:', priceText);

    // Parse price - handle various formats like $42.24, $73.92, etc.
    const priceMatch = priceText.match(/\$?(\d+\.?\d*)/);
    if (!priceMatch) {
        console.warn('Could not parse price from:', priceText);
        return getSelectedVariant();
    }

    const displayedPrice = parseFloat(priceMatch[1]);
    console.log('Parsed displayed price:', displayedPrice);

    // Find variant that matches this price (convert from cents)
    const matchingVariant = window.productVariants.find(variant => {
        const variantPrice = variant.price / 100;
        return Math.abs(variantPrice - displayedPrice) < 0.01; // Allow for small rounding differences
    });

    if (matchingVariant) {
        console.log('Found variant matching price:', matchingVariant);
        return matchingVariant;
    }

    // If no exact price match, try to determine from active size selector
    const activeSize = document.querySelector('.size-selector .active, ul li a.active');
    if (activeSize) {
        const sizeText = activeSize.textContent.trim().toLowerCase();
        const variantBySize = window.productVariants.find(variant => {
            return variant.title.toLowerCase().includes(sizeText.replace(' ', '-')) ||
                   variant.title.toLowerCase().includes(sizeText.replace(' ', ''));
        });
        if (variantBySize) {
            console.log('Found variant by active size:', variantBySize);
            return variantBySize;
        }
    }

    console.warn('Could not determine variant from price, using fallback');
    return getSelectedVariant();
}

/**
 * Handle variant change events
 */
function handleVariantChange(variantId) {
    console.log('Handling variant change for ID:', variantId);

    const variant = window.productVariants.find(v => v.id == variantId);
    if (variant) {
        console.log('Found variant:', variant);
        const productPrice = variant.price / 100; // Convert from cents

        // Update the price per can display
        updatePricePerCanText(variant.title, productPrice, window.currentProductId);

        // Also update the main product price if it's not updating automatically
        updateMainProductPrice(variant);

        // Update the hidden select to match the selected variant
        updateVariantSelector(variantId);
    } else {
        console.warn('Variant not found for ID:', variantId);
    }
}

/**
 * Update the main product price display
 */
function updateMainProductPrice(variant) {
    const productPriceElement = document.querySelector('#product-price, .price ins');
    const oldPriceElement = document.querySelector('#old-product-price, .price del');

    if (productPriceElement) {
        const formattedPrice = formatMoney(variant.price);
        productPriceElement.textContent = formattedPrice;
        console.log('Updated main product price to:', formattedPrice);
    }

    // Update compare at price if it exists
    if (oldPriceElement && variant.compare_at_price && variant.compare_at_price > variant.price) {
        const formattedComparePrice = formatMoney(variant.compare_at_price);
        oldPriceElement.textContent = formattedComparePrice;
        console.log('Updated compare at price to:', formattedComparePrice);
    }
}

/**
 * Update the variant selector to match the selected variant
 */
function updateVariantSelector(variantId) {
    const selectElement = document.querySelector('#product-select, select[name="id"]');
    if (selectElement) {
        selectElement.value = variantId;
        console.log('Updated variant selector to:', variantId);

        // Trigger change event to notify other scripts
        const changeEvent = new Event('change', { bubbles: true });
        selectElement.dispatchEvent(changeEvent);
    }
}

/**
 * Format price in money format (assumes USD)
 */
function formatMoney(cents) {
    const dollars = cents / 100;
    return '$' + dollars.toFixed(2);
}

/**
 * MIGRATION NOTES:
 * 
 * This script replaces the hardcoded pricing logic in pre_theme.js
 * 
 * Key improvements:
 * 1. All pricing data is centralized in PRODUCT_PRICING object
 * 2. Supports multivariant products (flavor + size combinations)
 * 3. Automatic price calculation for products without custom pricing
 * 4. Better error handling and logging
 * 5. Self-contained - creates display element if needed
 * 
 * To add new products:
 * 1. Add entry to PRODUCT_PRICING object with product ID
 * 2. Define custom prices for different pack sizes
 * 3. Products not in PRODUCT_PRICING will use automatic calculation
 * 
 * Variant title format requirements:
 * - Must include pack size as a number (e.g., "Yellow12", "alb24", "Spicy6")
 * - Number should preferably be at the end of the title
 * - Script will parse the number and use it for calculations
 */
